import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { LEGAL, isLegalFieldSet } from "../config/legal";
import { SITE_URL, WHATSAPP_DISPLAY, DELIVERY_DELAY } from "../config/site";

// Champ légal : affiche la valeur ou le placeholder à compléter (visible uniquement ici).
function F({ field }) {
  const v = LEGAL[field];
  return isLegalFieldSet(v) ? (
    <span>{v}</span>
  ) : (
    <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[0.85em] font-semibold text-amber-800">
      [À COMPLÉTER]
    </span>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl font-bold text-nuit-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-steel sm:text-base">{children}</div>
  </section>
);

const DOCS = {
  "mentions-legales": {
    title: "Mentions légales",
    meta: "Mentions légales du site Noveocard : éditeur, hébergement, propriété intellectuelle.",
    content: (
      <>
        <Section title="1. Éditeur du site">
          <p>
            Le site noveocard.fr est édité par : <F field="operateur" />, <F field="formeJuridique" />{" "}
            au capital de <F field="capital" />, immatriculée sous le SIRET <F field="siret" />.
          </p>
          <p>
            Siège social : <F field="adresse" /> — E-mail : <F field="email" /> — N° de TVA
            intracommunautaire : <F field="tva" />.
          </p>
          <p>
            Directeur de la publication : <F field="directeurPublication" />.
          </p>
        </Section>
        <Section title="2. Hébergement">
          <p>
            Le site est hébergé par : <F field="hebergeur" />.
          </p>
        </Section>
        <Section title="3. Contact">
          <p>
            Pour toute question, vous pouvez nous joindre sur WhatsApp au {WHATSAPP_DISPLAY}{" "}
            (réponse sous 30 minutes) ou par e-mail : <F field="email" />.
          </p>
        </Section>
        <Section title="4. Propriété intellectuelle">
          <p>
            L'ensemble des contenus du site (textes, visuels, logo Noveocard, interface) est protégé
            par le droit de la propriété intellectuelle. Toute reproduction ou représentation, totale
            ou partielle, sans autorisation préalable est interdite. Les logos et éléments d'identité
            visuelle transmis par les clients restent la propriété de leurs détenteurs.
          </p>
        </Section>
      </>
    ),
  },
  "conditions-generales-de-prestation": {
    title: "Conditions générales de prestation",
    meta: "Conditions générales de prestation Noveocard : commande, prix TTC, personnalisation, délais, droit de rétractation.",
    content: (
      <>
        <Section title="1. Objet">
          <p>
            Les présentes conditions régissent les prestations de conception et de fourniture de
            cartes digitales professionnelles personnalisées (carte PVC 86 × 54 mm équipée NFC)
            et de pages digitales associées, proposées par Noveocard (<F field="operateur" />,
            SIRET <F field="siret" />).
          </p>
        </Section>
        <Section title="2. Commande">
          <p>
            Toute commande s'effectue par échange sur WhatsApp au {WHATSAPP_DISPLAY}. La commande est
            réputée validée après confirmation du récapitulatif (design, contenu digital, quantité,
            prix) par le client dans la conversation. Aucun paiement ne s'effectue sur le site.
          </p>
        </Section>
        <Section title="3. Prix">
          <p>
            Les prix sont exprimés en euros toutes taxes comprises (TTC) : Carte Standard 40 € TTC,
            Carte Multi-liens 45 € TTC, Pack 4 cartes 80 € TTC. Les packs personnalisés font l'objet
            d'un tarif sur mesure communiqué avant validation. N° de TVA : <F field="tva" />.
          </p>
        </Section>
        <Section title="4. Personnalisation et validation">
          <p>
            Chaque carte est conçue à partir des éléments transmis par le client (logo, couleurs,
            nom, liens). Un aperçu est systématiquement soumis au client pour validation avant
            production. Aucune production n'est lancée sans validation expresse.
          </p>
        </Section>
        <Section title="5. Délais">
          <p>{DELIVERY_DELAY} Ces délais courent à compter de la validation du design par le client.</p>
        </Section>
        <Section title="6. Droit de rétractation">
          <p>
            Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne
            s'applique pas aux biens confectionnés selon les spécifications du consommateur ou
            nettement personnalisés. Les cartes Noveocard étant entièrement personnalisées, elles ne
            peuvent être ni reprises ni échangées après validation de l'aperçu, sauf défaut de
            conformité avéré.
          </p>
        </Section>
        <Section title="7. Garantie conformité">
          <p>
            En cas de défaut de fabrication ou de non-conformité à l'aperçu validé, la carte est
            remplacée sans frais. Toute réclamation s'effectue sur WhatsApp au {WHATSAPP_DISPLAY}.
          </p>
        </Section>
        <Section title="8. Responsabilité">
          <p>
            Noveocard fournit un support d'accès (carte) et une page digitale. Le client est
            responsable des contenus et liens qu'il demande de publier (exactitude, droits, légalité).
            Noveocard ne saurait être tenue responsable des contenus des sites tiers liés.
          </p>
        </Section>
      </>
    ),
  },
  cgu: {
    title: "Conditions générales d'utilisation",
    meta: "Conditions générales d'utilisation du site Noveocard.",
    content: (
      <>
        <Section title="1. Objet">
          <p>
            Les présentes CGU encadrent l'utilisation du site noveocard.fr, site vitrine de la marque
            Noveocard (<F field="operateur" />). Le site présente des cartes digitales
            professionnelles et permet leur configuration ; toute commande se conclut sur WhatsApp.
          </p>
        </Section>
        <Section title="2. Accès au site">
          <p>
            Le site est accessible gratuitement, 24h/24, sous réserve des opérations de maintenance.
            L'éditeur ne garantit pas une disponibilité ininterrompue.
          </p>
        </Section>
        <Section title="3. Utilisation du configurateur">
          <p>
            Le configurateur produit un aperçu indicatif. Le logo importé reste sur l'appareil de
            l'utilisateur (aucun envoi serveur) ; sa transmission s'effectue par la conversation
            WhatsApp. L'utilisateur garantit détenir les droits sur les éléments qu'il transmet.
          </p>
        </Section>
        <Section title="4. Propriété intellectuelle">
          <p>
            Toute reproduction non autorisée des contenus du site est interdite. La marque et le logo
            Noveocard sont la propriété de <F field="operateur" />.
          </p>
        </Section>
        <Section title="5. Responsabilité">
          <p>
            L'éditeur s'efforce de fournir des informations exactes (prix TTC, délais) sans pouvoir
            garantir l'absence d'erreur. Les liens externes (WhatsApp, réseaux sociaux) relèvent de
            la responsabilité de leurs éditeurs.
          </p>
        </Section>
      </>
    ),
  },
  "politique-de-confidentialite": {
    title: "Politique de confidentialité (RGPD)",
    meta: "Politique de confidentialité Noveocard : données traitées, base légale, durées, droits RGPD.",
    content: (
      <>
        <Section title="1. Responsable de traitement">
          <p>
            Le responsable de traitement est <F field="operateur" /> — <F field="adresse" /> — e-mail :{" "}
            <F field="email" />.
          </p>
        </Section>
        <Section title="2. Données traitées">
          <p>
            Le site ne comporte ni compte client, ni paiement en ligne, ni formulaire collectant des
            données personnelles. Les seules données personnelles échangées le sont dans le cadre de
            la conversation WhatsApp initiée librement par l'utilisateur (nom, coordonnées
            professionnelles, éléments de personnalisation comme le logo).
          </p>
          <p>
            Le logo importé dans le configurateur reste stocké localement dans votre navigateur et
            n'est jamais transmis à nos serveurs.
          </p>
        </Section>
        <Section title="3. Finalités et bases légales">
          <p>
            Les échanges WhatsApp servent exclusivement à répondre à vos demandes et à exécuter votre
            commande (base légale : mesures précontractuelles et exécution du contrat). Aucune donnée
            n'est vendue, louée ou partagée à des tiers à des fins commerciales.
          </p>
        </Section>
        <Section title="4. Sous-traitants">
          <p>
            WhatsApp (Meta Platforms) agit comme prestataire de messagerie ; son utilisation est
            soumise à ses propres conditions. L'hébergement du site est assuré par{" "}
            <F field="hebergeur" />.
          </p>
        </Section>
        <Section title="5. Durées de conservation">
          <p>
            Les conversations sont conservées le temps nécessaire au traitement de la commande et au
            suivi de la relation commerciale, conformément aux durées légales applicables.
          </p>
        </Section>
        <Section title="6. Vos droits">
          <p>
            Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, de
            portabilité et d'opposition sur vos données. Pour les exercer : WhatsApp au{" "}
            {WHATSAPP_DISPLAY} ou e-mail <F field="email" />. Vous pouvez également introduire une
            réclamation auprès de la CNIL (cnil.fr).
          </p>
        </Section>
      </>
    ),
  },
  "politique-cookies": {
    title: "Politique cookies",
    meta: "Politique cookies Noveocard : aucun cookie tiers sans consentement, mesure d'audience anonyme optionnelle.",
    content: (
      <>
        <Section title="1. Principe">
          <p>
            Noveocard applique une politique cookies minimaliste : aucun cookie publicitaire, aucun
            traçage tiers, et aucun script de mesure d'audience n'est chargé avant votre consentement
            explicite via la bannière affichée lors de votre première visite.
          </p>
        </Section>
        <Section title="2. Stockage strictement nécessaire">
          <p>
            Seul votre choix de consentement est mémorisé (stockage local de votre navigateur), afin
            de ne pas vous redemander votre préférence à chaque visite. Ce stockage ne contient
            aucune donnée personnelle.
          </p>
        </Section>
        <Section title="3. Mesure d'audience (optionnelle)">
          <p>
            Si vous acceptez, un outil de mesure d'audience respectueux de la vie privée (type
            Plausible, sans cookie ni identifiant personnel) peut être activé pour comprendre
            l'usage du site : pages vues, clics sur les boutons WhatsApp, usage du configurateur. Si
            vous refusez, rien n'est chargé — le refus est aussi simple qu'un clic.
          </p>
        </Section>
        <Section title="4. Modifier votre choix">
          <p>
            Pour modifier votre consentement, supprimez le stockage local du site dans les paramètres
            de votre navigateur : la bannière réapparaîtra lors de votre prochaine visite.
          </p>
        </Section>
      </>
    ),
  },
};

export default function Legal({ doc }) {
  const d = DOCS[doc];
  return (
    <>
      <Seo title={d.title} description={d.meta} path={`/${doc}`} />
      <PageHero eyebrow="Informations légales" titleLines={[d.title]} />
      <section className="bg-white pb-20 sm:pb-28">
        <div className="container-nv max-w-3xl" data-testid={`legal-${doc}`}>
          {d.content}
          <p className="mt-12 border-t border-nuit-900/10 pt-6 text-xs text-steel">
            Dernière mise à jour : juillet 2026. Les champs marqués [À COMPLÉTER] seront renseignés
            avant la mise en ligne définitive — voir le fichier de configuration du site.
          </p>
        </div>
      </section>
    </>
  );
}
